import {
  ArrowsInIcon,
  ArrowsOutIcon,
  Col,
  DisplayText,
  LoadingSpinner,
  Row
} from '@paypalcorp/pp-react';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React, { useContext, useRef, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { isUserLoggedInSelector } from '../../store/App/selectors';

import TestPanel from '../TestPanel';
import ControlPanel from './ControlPanel';
import HtmlPageSection from './HtmlPageSection';
import styles from './IntegrationPageSection.module.less';
import IntegrationPageSectionContext from './StateManagement/IntegrationPageSectionContext';
import TabbedLayout from './TabbedLayout';
import tabs from '../../util/sectionTabs';
import { trackButtonClick } from '../../lib/fpti';
const SectionContent = ({ ecmIntegContent = {} }) => {
  const { isLoading } = useContext(IntegrationPageSectionContext);

  const isUserLoggedIn = useSelector(isUserLoggedInSelector);

  const [selectedTabIdx, setSelectedTabIdx] = useState(0);
  const [expandedMode, setExpandedMode] = useState(false);
  const prevTabSelectionRef = useRef(0);
  const prevScrollRef = useRef(null);
  const { hash } = useLocation();
  const previewPanelTabs =
    ecmIntegContent.contents.find((section) => section.key === 'sectionTabs')?.contentJson ?? tabs;

  useEffect(() => {
    if (hash) {
      const matchingAnchor = ecmIntegContent.contents.find(
        (section) => section.anchor === hash.slice(1)
      );
      if (matchingAnchor?.content?.codeBlockKey) {
        setSelectedTabIdx(1);
      }
    }
  }, [hash]);

  const toggleExpand = (expand) => {
    if (expand) {
      trackButtonClick('Expand');
      prevScrollRef.current = window.scrollY;
      /* We store the previous scroll value as we modify the scroll for the expanded mode,
         however we need to set this value back when the user collapses to maintain the scroll context
         of the user */
      setSelectedTabIdx(0);
    } else {
      trackButtonClick('Collapse');
      setSelectedTabIdx(prevTabSelectionRef.current);
    }
    setExpandedMode(expand);
  };

  const handleTabSelect = (e, idx) => {
    prevTabSelectionRef.current = idx;
    const tabLabel = Object.values(tabs)[prevTabSelectionRef.current]?.label;
    if (tabLabel) trackButtonClick(tabLabel);
    setSelectedTabIdx(idx);
  };

  const dynamicColumnWidth = expandedMode ? 12 : 6;

  return (
    <>
      <ControlPanel />
      {isLoading && (
        <div className={styles.integration_loader_container}>
          <div className={styles.integration_loader}>
            <LoadingSpinner screenReaderText="loading" size="lg" />
          </div>
        </div>
      )}
      <Row
        className={isLoading ? styles.integration_hidden : styles.page_section_container}
        noGutters
      >
        <Col className={cx([styles.page_section, { [styles.page_section__hidden]: expandedMode }])}>
          {ecmIntegContent?.title && (
            <div className={styles.heading_container} data-testid="integrate-title">
              <DisplayText size="sm" type="regular">
                {ecmIntegContent?.title}
              </DisplayText>
            </div>
          )}
          <HtmlPageSection ecmContent={ecmIntegContent} />
        </Col>
        <Col
          xs={12}
          sm={12}
          md={12}
          lg={dynamicColumnWidth}
          xl={dynamicColumnWidth}
          xxl={dynamicColumnWidth}
          className={cx([
            styles.page_section,
            { [styles.integration_code_container__logged_out]: !isUserLoggedIn },
            { [styles.integration_code_container__logged_in]: isUserLoggedIn }
          ])}
        >
          <TabbedLayout
            expandedMode={expandedMode}
            selectedTabIdx={selectedTabIdx}
            handleTabSelect={handleTabSelect}
            prevScroll={prevScrollRef.current}
            tabs={previewPanelTabs}
          />
          {expandedMode ? (
            <ArrowsInIcon
              data-testid="collapse-button"
              className={cx([
                styles.integration_expand_button__base,
                styles.integration_collapse_button
              ])}
              onClick={() => toggleExpand(false)}
            />
          ) : (
            <ArrowsOutIcon
              data-testid="expand-button"
              className={cx([
                styles.integration_expand_button__base,
                styles.integration_expand_button
              ])}
              onClick={() => toggleExpand(true)}
            />
          )}
        </Col>
      </Row>
      <TestPanel />
    </>
  );
};

SectionContent.propTypes = {
  ecmIntegContent: PropTypes.object.isRequired
};

export default SectionContent;
